import { Col, Row, FormLabel, InputGroup, FormControl } from "react-bootstrap";
import { IFieldEditor, IMediaTreeItem } from "../../../../models/Interfaces";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faRefresh, faTimes } from "@fortawesome/free-solid-svg-icons";
import TreeSelect from "../../../components/common/TreeSelect";
import { useSiteMedia } from "../../../../hooks/useSiteMedia";
import { TreeItemSort } from "../../../../config/Enums";
import AppEmptyState from "../../../components/common/AppEmptyState";
import { MediaValueHelper } from "../../../../helpers/MediaValueHelper";


const ImageSelectEditor = (props: IFieldEditor) => {

  const media = useSiteMedia();
  const siteMedia = media.items ?? [];
  const selectedMediaUrl = MediaValueHelper.getUrl(props.property.PublishedValue);

  const resolveSelectedMedia = (value: string): IMediaTreeItem | null => {
    return siteMedia.find((item) => item.url === value) ?? null;
  };
  
  
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    props.onValueChange(props.property.Id, value);
  };

  const handleTreeSelect = (value: string) : void => {
    if (value) {
      const selectedMedia = resolveSelectedMedia(value);
      props.onValueChange(
        props.property.Id,
        MediaValueHelper.create(selectedMedia?.itemid ?? null, value)
      );
    }
  };

  const mediaIsVideo = () : boolean => {
    const videoMimeTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/mov', 'video/avi', 'video/mpeg'];
    const mediaUrl = selectedMediaUrl;

    if (mediaUrl) {
      const fileType = mediaUrl.toLowerCase();
    
      if (videoMimeTypes.includes(fileType)) {
        return true;
      }
    
      // Fallback to check file extension if MIME type is not available
      const extension = mediaUrl.split('.').pop()?.toLowerCase();
      const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mpeg'];
    
      if (extension && videoExtensions.includes(extension)) {
        return true;
      }
    }
    return false;
  }

  return (
    <>
        <Row className="m-0 p-0" name="ImageSelectEditor">
          <Col md={12} xl={6} className="m-0 p-0">
            <FormLabel>{props.property.DisplayLabel}</FormLabel>
            <InputGroup>
              <FormControl 
                type="text"
                placeholder="enter an image url or select from uploaded media..."
                value={selectedMediaUrl}
                onChange={handleInputChange}>
              </FormControl>
              <InputGroupText >
                <div title="clear image" onClick={() => props.onValueChange(props.property.Id, "")} style={{cursor: "pointer"}}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </InputGroupText>
            </InputGroup>
            <div className="d-flex align-items-center gap-2 mt-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={media.refresh} disabled={media.refreshing} aria-label="Refresh site images">
                <FontAwesomeIcon icon={faRefresh} spin={media.refreshing} /> Refresh
              </button>
              {media.refreshing && <span className="small text-muted" role="status">{media.items === undefined ? "Loading media…" : "Updating media…"}</span>}
            </div>
            {media.error && <div className="small text-warning mt-2" role="status">{media.error}</div>}
            {media.items?.length === 0 && <div className="small text-muted mt-2">No site media found. Upload media or use an external URL.</div>}
            { siteMedia.length > 0 && (
              <InputGroup className="mt-3 w-100">
                <TreeSelect 
                  idKey="id"
                  valueKey="url" 
                  typeKey="type"
                  displayKey="text" 
                  parentKey="parent"
                  selectnodetype="file"
                  initialValue={selectedMediaUrl} 
                  items={siteMedia} 
                  onItemSelect={handleTreeSelect} 
                  openText="select site image"
                  closeText="close image list"
                  style={{ flexGrow: 1}}
                  rootNodeKeyValue={"#"}
                  closeOnSelect={false}
                  hideClear={true}
                  triggerClassName="page-selector__trigger"
                  popoverTitle="Select Media"
                  sortBy={TreeItemSort.Ascending} />

              </InputGroup>
            )}
          </Col>
          <Col md={12} xl={6} className="m-0 p-0 p-3 text-center" name="MediaPreview">
            {props.property.PublishedValue && (
              mediaIsVideo() ? (
                <video
                  src={selectedMediaUrl}
                  className="img-fluid mw-100"
                  style= {{ maxHeight: "190px "}}
                  controls
                ></video>
              ) : (
                <img
                  src={selectedMediaUrl}
                  className="img-fluid mw-100"
                  style= {{ maxHeight: "190px "}}
                  alt="Media"
                />
              )
            )}
            {!props.property.PublishedValue && (
              <AppEmptyState
                icon={faImage}
                title="No media selected"
                message="Choose an uploaded image or paste a media URL to preview it here."
              />
            )}
          </Col>
        </Row>
    </>
  );
}

export default ImageSelectEditor;
